import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { 
  Search, ShoppingCart, Wrench, Zap, TrendingUp, Crown, 
  Fuel, DollarSign, Info, Package 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ToolStore = () => {
  const { user, token: authToken } = useAuth();
  
  // State management
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [ownedTools, setOwnedTools] = useState([]);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [nftOnly, setNftOnly] = useState(false);
  const [sortBy, setSortBy] = useState('price_low');

  useEffect(() => {
    fetchUserProfile();
    fetchOwnedTools();
    fetchTools();
  }, [selectedCategory, selectedTier, nftOnly, sortBy]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/member/game/profile`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.ok) {
        setUserCredits(data.player.available_credits || 0);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchOwnedTools = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/member/tools/inventory/mine`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.ok) {
        setOwnedTools(data.tools || []);
      }
    } catch (error) {
      console.error('Error fetching owned tools:', error);
    }
  };

  const fetchTools = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedTier !== 'all') {
        params.append('tier', selectedTier);
      }
      if (nftOnly) {
        params.append('nft_ready', 'true');
      }

      const response = await fetch(
        `${BACKEND_URL}/api/member/tools/catalog?${params}`,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        let sortedTools = [...data.tools];
        
        // Apply sorting
        switch (sortBy) {
          case 'price_low':
            sortedTools.sort((a, b) => a.purchase_price - b.purchase_price);
            break;
          case 'price_high':
            sortedTools.sort((a, b) => b.purchase_price - a.purchase_price);
            break;
          case 'name':
            sortedTools.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case 'tier':
            const tierOrder = { starter: 1, professional: 2, premium: 3, elite: 4, legendary: 5 };
            sortedTools.sort((a, b) => tierOrder[a.tier] - tierOrder[b.tier]);
            break;
          default:
            break;
        }
        
        // Apply search filter
        if (searchQuery) {
          sortedTools = sortedTools.filter(tool =>
            tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            tool.description.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        
        setTools(sortedTools);
      }
    } catch (error) {
      console.error('Error fetching tools:', error);
      toast.error("Failed to load tools. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseTool = async (toolCode) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/member/tools/${toolCode}/purchase`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        toast.success(data.message || "Tool purchased successfully!");
        // Refresh data
        fetchUserProfile();
        fetchOwnedTools();
        fetchTools();
      } else {
        toast.error(data.detail || "Failed to purchase tool");
      }
    } catch (error) {
      console.error('Error purchasing tool:', error);
      toast.error("Failed to purchase tool. Please try again.");
    }
  };

  const isToolOwned = (toolCode) => {
    return ownedTools.some(ot => ot.owned_tool.tool_code === toolCode);
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      'starter': 'bg-green-100 text-green-800',
      'professional': 'bg-blue-100 text-blue-800',
      'premium': 'bg-purple-100 text-purple-800',
      'elite': 'bg-red-100 text-red-800',
      'legendary': 'bg-yellow-100 text-yellow-800'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'traffic': TrendingUp,
      'content': Package,
      'followup': Zap,
      'landing': Wrench,
      'networking': DollarSign,
      'limited': Crown
    };
    return icons[category] || Package;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tool Store</h1>
        <p className="text-gray-600">Invest in marketing tools to boost your lead generation and conversions</p>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">{formatNumber(userCredits)}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Available Credits</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Package className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">{tools.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Tools Available</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <ShoppingCart className="h-5 w-5 text-purple-500" />
              <span className="text-2xl font-bold text-gray-900">{ownedTools.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Owned Tools</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">{tools.filter(t => t.nft_ready).length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">NFT-Ready Tools</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="traffic">Traffic</SelectItem>
                  <SelectItem value="content">Content</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                  <SelectItem value="landing">Landing Page</SelectItem>
                  <SelectItem value="networking">Networking</SelectItem>
                  <SelectItem value="limited">Limited Edition</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tier Filter */}
            <div>
              <Select value={selectedTier} onValueChange={setSelectedTier}>
                <SelectTrigger>
                  <SelectValue placeholder="All Tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                  <SelectItem value="legendary">Legendary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* NFT Filter */}
            <div>
              <Button
                variant={nftOnly ? "default" : "outline"}
                onClick={() => setNftOnly(!nftOnly)}
                className="w-full"
              >
                <Crown className="h-4 w-4 mr-2" />
                NFT Only
              </Button>
            </div>

            {/* Sort Filter */}
            <div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="tier">Tier</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tools Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <Card key={index} className="animate-pulse">
              <CardContent className="p-0">
                <div className="h-48 bg-gray-300 rounded-t-lg"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded w-full"></div>
                  <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : tools.length === 0 ? (
        <Card className="p-12 text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No tools found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your filters to see more tools</p>
          <Button onClick={() => {
            setSearchQuery('');
            setSelectedCategory('all');
            setSelectedTier('all');
            setNftOnly(false);
          }}>
            Clear Filters
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {tools.map((tool) => {
            const CategoryIcon = getCategoryIcon(tool.category);
            const owned = isToolOwned(tool.code);
            const canAfford = userCredits >= tool.purchase_price;
            
            return (
              <Card key={tool.id} className="group hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  {/* Tool Header */}
                  <div className="relative h-48 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-t-lg overflow-hidden">
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="text-6xl mb-2">{tool.icon}</div>
                        <CategoryIcon className="h-6 w-6 text-blue-500 mx-auto" />
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="absolute top-2 left-2 space-y-1">
                      <Badge className={getTierBadgeColor(tool.tier)}>
                        {tool.tier}
                      </Badge>
                      {tool.nft_ready && (
                        <Badge className="bg-yellow-500 text-white flex items-center">
                          <Crown className="h-3 w-3 mr-1" />
                          NFT
                        </Badge>
                      )}
                      {owned && (
                        <Badge className="bg-green-500 text-white">
                          Owned
                        </Badge>
                      )}
                    </div>

                    {/* Price Badge */}
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-white text-gray-900 text-lg font-bold">
                        {formatNumber(tool.purchase_price)} ₡
                      </Badge>
                    </div>
                  </div>

                  {/* Tool Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-1">{tool.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{tool.description}</p>

                    {/* Stats */}
                    <div className="space-y-2 mb-4 text-xs">
                      {tool.quality_boost > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Quality Boost:</span>
                          <span className="font-semibold text-green-600">+{(tool.quality_boost * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      {tool.quantity_boost > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Quantity Boost:</span>
                          <span className="font-semibold text-blue-600">+{(tool.quantity_boost * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      {tool.conversion_boost > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Conversion Boost:</span>
                          <span className="font-semibold text-purple-600">+{(tool.conversion_boost * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </div>

                    {/* Fuel Info */}
                    {tool.fuel_type !== 'none' && (
                      <div className="flex items-center text-xs text-gray-600 mb-4">
                        <Fuel className="h-3 w-3 mr-1" />
                        <span>Fuel: {formatNumber(tool.fuel_cost)} ₡ / {tool.fuel_frequency}</span>
                      </div>
                    )}

                    {/* NFT Supply Info */}
                    {tool.nft_ready && tool.total_supply && (
                      <div className="mb-4 p-2 bg-yellow-50 rounded text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Limited Supply:</span>
                          <span className="font-semibold text-yellow-700">
                            {tool.minted_count}/{tool.total_supply}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {owned ? (
                        <Button 
                          className="w-full" 
                          variant="secondary"
                          onClick={() => window.location.href = '/member/tools/inventory'}
                        >
                          View in Inventory
                        </Button>
                      ) : (
                        <>
                          <Button 
                            className="w-full" 
                            onClick={() => handlePurchaseTool(tool.code)}
                            disabled={!canAfford}
                          >
                            <ShoppingCart className="h-4 w-4 mr-2" />
                            {canAfford ? 'Purchase' : 'Insufficient Credits'}
                          </Button>
                          <Button 
                            className="w-full" 
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedTool(tool)}
                          >
                            <Info className="h-4 w-4 mr-2" />
                            Details
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tool Details Modal */}
      {selectedTool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedTool.name}</h2>
                  <div className="flex gap-2">
                    <Badge className={getTierBadgeColor(selectedTool.tier)}>
                      {selectedTool.tier}
                    </Badge>
                    {selectedTool.nft_ready && (
                      <Badge className="bg-yellow-500 text-white">NFT-Ready</Badge>
                    )}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedTool(null)}
                >
                  ✕
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600">{selectedTool.long_description}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Pricing</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(selectedTool.purchase_price)} Credits
                  </p>
                  {selectedTool.resale_percentage > 0 && (
                    <p className="text-sm text-gray-600">
                      Resale Value: {(selectedTool.resale_percentage * 100).toFixed(0)}% ({formatNumber(selectedTool.purchase_price * selectedTool.resale_percentage)} credits)
                    </p>
                  )}
                </div>

                {selectedTool.fuel_type !== 'none' && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Fuel & Maintenance</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>Fuel Type: {selectedTool.fuel_type.replace('_', ' ')}</p>
                      <p>Fuel Cost: {formatNumber(selectedTool.fuel_cost)} credits per {selectedTool.fuel_frequency}</p>
                      {selectedTool.decay_days && (
                        <p>Decay Period: {selectedTool.decay_days} days without fuel</p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Tool Effects</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {selectedTool.quality_boost > 0 && (
                      <div className="p-3 bg-green-50 rounded">
                        <p className="text-2xl font-bold text-green-600">+{(selectedTool.quality_boost * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-600">Quality</p>
                      </div>
                    )}
                    {selectedTool.quantity_boost > 0 && (
                      <div className="p-3 bg-blue-50 rounded">
                        <p className="text-2xl font-bold text-blue-600">+{(selectedTool.quantity_boost * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-600">Quantity</p>
                      </div>
                    )}
                    {selectedTool.conversion_boost > 0 && (
                      <div className="p-3 bg-purple-50 rounded">
                        <p className="text-2xl font-bold text-purple-600">+{(selectedTool.conversion_boost * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-600">Conversion</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTool.upgradable && (
                  <div className="p-4 bg-blue-50 rounded">
                    <h3 className="font-semibold text-gray-900 mb-1">Upgradable</h3>
                    <p className="text-sm text-gray-600">
                      This tool can be upgraded for {formatNumber(selectedTool.upgrade_cost)} credits
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  {isToolOwned(selectedTool.code) ? (
                    <Button className="flex-1" variant="secondary" onClick={() => window.location.href = '/member/tools/inventory'}>
                      View in Inventory
                    </Button>
                  ) : (
                    <Button 
                      className="flex-1" 
                      onClick={() => {
                        handlePurchaseTool(selectedTool.code);
                        setSelectedTool(null);
                      }}
                      disabled={userCredits < selectedTool.purchase_price}
                    >
                      Purchase for {formatNumber(selectedTool.purchase_price)} Credits
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setSelectedTool(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ToolStore;
